/* eslint sort-keys: 0 */
import {
  CONTROL_BORDER_HIGH_CONTRAST_COLOR,
  CONTROL_COLOR_BLUE_300,
  CONTROL_COLOR_BLUE_600,
  CONTROL_COLOR_BLUE_700,
  CONTROL_COLOR_BLUE_800,
  CONTROL_COLOR_GREY_400,
  CONTROL_COLOR_RED_500,
  CONTROL_FORM_TEXT_COLOR,
  CONTROL_PRIMARY_COLOR,
  CONTROL_PRIMARY_TEXT_COLOR,
  CONTROL_SECONDARY_COLOR,
  CONTROL_SUPPORTING_TEXT_COLOR,
  CONTROL_TERMS_COLOR,
  PAYPAL_HELVETICA_FONT,
  PP_ERROR_OUTLINE_COLOR,
  SOLD_OUT_TAG_COLOR,
  WHITE_COLOR,
} from '../styleVariables';

export const controlTheme: AppTheme.WeasleyTheme = {
  button: {
    closeButton: {
      borderRadius: '50%',
      color: CONTROL_PRIMARY_TEXT_COLOR,
      height: '35px',
      padding: 'unset',
      width: '35px',
      hover: {
        color: 'transparent',
        background: 'transparent',
      },
      focus: {
        background: 'transparent',
        border: `1px solid ${CONTROL_PRIMARY_COLOR}`,
        boxShadow: `0 0 0 2px ${CONTROL_PRIMARY_COLOR}, 0 0 0 6px ${CONTROL_COLOR_BLUE_300}`,
        borderRadius: '50%',
      },
    },
    active: {
      background: CONTROL_COLOR_BLUE_600,
      border: `1px solid ${CONTROL_COLOR_BLUE_600}`,
    },
    default: {
      background: CONTROL_PRIMARY_COLOR,
      border: `1px solid ${CONTROL_PRIMARY_COLOR}`,
      color: WHITE_COLOR,
      font_size: '15px',
      text_decoration: 'unset',
      weight: 500,
    },
    disabled: {
      color: CONTROL_COLOR_GREY_400,
    },
    focus: {
      background: CONTROL_COLOR_BLUE_700,
      border: `1px solid ${CONTROL_COLOR_BLUE_700}`,
      box_shadow: 'none',
      text_decoration: 'underline',
    },
    hover: {
      background: CONTROL_COLOR_BLUE_700,
      border: `1px solid ${CONTROL_SECONDARY_COLOR}`,
      text_decoration: 'underline',
    },
    variant: {
      outlined: {
        active: {
          background: 'transparent',
          border: CONTROL_COLOR_BLUE_800,
          color: CONTROL_COLOR_BLUE_800,
        },
        default: {
          background: 'transparent',
          border: `2px solid ${CONTROL_PRIMARY_COLOR}`,
          color: CONTROL_PRIMARY_COLOR,
        },
        focus: {
          background: 'transparent',
          border: `2px solid ${CONTROL_COLOR_BLUE_600}`,
          box_shadow: `0 0 0 4px ${CONTROL_COLOR_BLUE_300}`,
          color: CONTROL_COLOR_BLUE_600,
          text_decoration: 'underline',
        },
        hover: {
          background: 'transparent',
          border: `2px solid ${CONTROL_COLOR_BLUE_800}`,
          color: CONTROL_COLOR_BLUE_800,
          text_decoration: 'underline',
        },
      },
      text: {
        default: {
          background: 'transparent',
          border: 'none',
          color: CONTROL_PRIMARY_COLOR,
          fontSize: '15px',
        },
        focus: {
          background: 'transparent',
          border: `2px solid ${CONTROL_COLOR_BLUE_600}`,
          border_radius: '4px',
          box_shadow: `0 0 0 4px ${CONTROL_COLOR_BLUE_300}`,
          color: CONTROL_COLOR_BLUE_600,
          text_decoration: 'underline',
        },
        hover: {
          background: 'transparent',
          border: 'none',
          color: CONTROL_COLOR_BLUE_800,
          text_decoration: 'underline',
        },
      },
    },
  },
  input: {
    default: {
      color: CONTROL_FORM_TEXT_COLOR,
      border: `1px solid ${CONTROL_COLOR_GREY_400}`,
      boxShadow: 'none',
    },
    focus: {
      color: CONTROL_FORM_TEXT_COLOR,
      border: `1px solid ${CONTROL_PRIMARY_COLOR}`,
      boxShadow: `0 0 0 2px ${CONTROL_PRIMARY_COLOR}, 0 0 0 6px ${CONTROL_COLOR_BLUE_300}`,
    },
    error: {
      default: {
        border: `1px solid ${CONTROL_COLOR_RED_500}`,
        boxShadow: 'none',
      },
      focus: {
        border: `2px solid ${CONTROL_COLOR_RED_500}`,
        boxShadow: `${CONTROL_COLOR_RED_500} 0px 0px 0px 1px inset, ${PP_ERROR_OUTLINE_COLOR} 0px 0px 0px 5px`,
      },
    },
  },
  checkbox: {
    container: {
      default: { paddingBottom: '16px' },
      terms: { paddingBottom: 'unset' },
    },
    input: {
      checked: {
        focus: {
          labelBefore: {
            background: CONTROL_PRIMARY_COLOR,
            border: `1px solid ${CONTROL_PRIMARY_COLOR}`,
            borderRadius: '2px',
            boxShadow: 'none',
          },
        },
        noFocus: {
          labelBefore: {
            background: CONTROL_PRIMARY_COLOR,
            border: `1px solid ${CONTROL_PRIMARY_COLOR}`,
            borderRadius: '2px',
          },
        },
      },
      noChecked: {
        focus: {
          labelBefore: {
            border: `1px solid ${CONTROL_PRIMARY_COLOR}`,
            borderRadius: '2px',
            boxShadow: 'none',
          },
        },
      },
    },
    label: {
      after: { top: '7px' },
      before: {
        border: `1px solid ${CONTROL_BORDER_HIGH_CONTRAST_COLOR}`,
        borderRadius: '2px',
        top: '3px',
      },
      default: {
        color: CONTROL_FORM_TEXT_COLOR,
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '24px',
      },
      terms: {
        color: CONTROL_TERMS_COLOR,
        fontSize: '13px',
        fontWeight: 400,
        lineHeight: '20px',
      },
    },
  },
  link: {
    default: {
      color: CONTROL_PRIMARY_COLOR,
      lineHeight: 'unset',
    },
    hover: { color: CONTROL_PRIMARY_COLOR },
    focus: {
      borderRadius: 'unset',
      boxShadow: 'unset',
      outline: 'revert',
    },
    legalLink: {
      color: CONTROL_TERMS_COLOR,
      fontWeight: 'unset',
      fontSize: 'unset',
    },
  },
  themeType: 'controlTheme',
  loadingSkeleton: {
    height: '48px',
    viewBoxHeight: '48',
  },
  typography: {
    caption: { color: CONTROL_SUPPORTING_TEXT_COLOR },
    caption_light: { color: CONTROL_SUPPORTING_TEXT_COLOR },
    font: { family: PAYPAL_HELVETICA_FONT },
    headingH1: { color: CONTROL_PRIMARY_TEXT_COLOR },
    headingH2: { color: CONTROL_PRIMARY_TEXT_COLOR },
    body: { color: CONTROL_PRIMARY_TEXT_COLOR },
    textHelper: { color: CONTROL_SUPPORTING_TEXT_COLOR },
    fontSize: {
      xLarge: '28px',
      large: '24px',
      medium: '22px',
      regular: '18px',
      small: '14px',
    },
    lineHeight: {
      xLarge: '36px',
      large: '24px',
      medium: '20px',
    },
    weight: {
      xBold: 700,
      bold: 500,
      regular: 400,
    },
  },
  soldoutTag: {
    border: `2px solid ${SOLD_OUT_TAG_COLOR}`,
    borderColor: `transparent transparent ${SOLD_OUT_TAG_COLOR} ${SOLD_OUT_TAG_COLOR}`,
    color: SOLD_OUT_TAG_COLOR,
  },
  wrappers: {
    content: {
      maxWidth: '375px',
    },
  },
  WHITE_COLOR,
  RED_COLOR: CONTROL_COLOR_RED_500,
};
